import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Loader2, X, Search, Download, Upload, ChevronUp, ChevronDown, ArrowUpDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

const PAGE_SIZE = 50;

function StatCard({ label, value, sub, tone }) {
  const toneClass = tone === "red" ? "text-rose-600" : tone === "green" ? "text-emerald-600" : "text-slate-900";
  return (
    <Card className="border-slate-200">
      <CardContent className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p className={`mt-2 text-3xl font-bold tracking-tight ${toneClass}`}>{value}</p>
        {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function SortIcon({ dir }) {
  if (dir === "asc") return <ChevronUp className="h-3.5 w-3.5" />;
  if (dir === "desc") return <ChevronDown className="h-3.5 w-3.5" />;
  return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
}

export default function StickInventory({ canWrite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [osFilter, setOsFilter] = useState("");
  const [noImeiOnly, setNoImeiOnly] = useState(false);
  const [sort, setSort] = useState({ key: "serial", dir: "asc" });
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stick-inventory", { params: { page_size: 5000 } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const osBuildList = useMemo(() => {
    const set = new Set();
    items.forEach((s) => { if (s.os_build_version) set.add(s.os_build_version); });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((s) => {
      if (noImeiOnly && s.imei1) return false;
      if (osFilter && s.os_build_version !== osFilter) return false;
      if (!query) return true;
      return (
        (s.serial || "").toLowerCase().includes(query) ||
        (s.os_build_version || "").toLowerCase().includes(query) ||
        (s.imei1 || "").toLowerCase().includes(query) ||
        (s.iccid || "").toLowerCase().includes(query) ||
        (s.mac_id || "").toLowerCase().includes(query)
      );
    });
  }, [items, q, osFilter, noImeiOnly]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const av = (a[key] || "").toString().toLowerCase();
      const bv = (b[key] || "").toString().toLowerCase();
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageItems = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  function toggleSort(key) {
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  }

  const totalSticks = items.length;
  const missingImei = items.filter((s) => !s.imei1).length;
  const osTypes = osBuildList.length;
  const complete = items.filter((s) => s.imei1 && s.os_build_version).length;

  function downloadCsv() {
    const headers = ["Serial", "OS Build Version", "IMEI1", "ICCID", "MAC ID", "Created Date"];
    const rows = sorted.map((s) => [
      s.serial || "",
      s.os_build_version || "",
      s.imei1 || "",
      s.iccid || "",
      s.mac_id || "",
      (s.created_at || "").slice(0, 10),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => {
      const str = String(v);
      return /[,"\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `stick-inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Sticks" value={totalSticks} sub="in inventory" />
        <StatCard label="Missing IMEI" value={missingImei} sub="sticks without IMEI" tone={missingImei > 0 ? "red" : undefined} />
        <StatCard label="OS Build Types" value={osTypes} sub="unique builds" />
        <StatCard label="Complete Records" value={complete} sub="with IMEI & OS build" tone="green" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search serial, IMEI, OS build…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="sticks-search"
          />
        </div>
        <select
          value={osFilter}
          onChange={(e) => setOsFilter(e.target.value)}
          className="flex h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm"
          data-testid="sticks-os-filter"
        >
          <option value="">All OS builds</option>
          {osBuildList.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
          <Checkbox checked={noImeiOnly} onCheckedChange={(v) => setNoImeiOnly(!!v)} data-testid="sticks-no-imei-only" />
          No IMEI
        </label>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="sticks-download-btn">
            <Download className="mr-1 h-4 w-4" /> Download CSV
          </Button>
          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="sticks-bulk-btn">
              <Upload className="mr-1 h-4 w-4" /> Bulk Import
            </Button>
          )}
          {canWrite && (
            <Button size="sm" onClick={() => setEditing("new")} data-testid="sticks-add-btn">
              <Plus className="mr-1 h-4 w-4" /> Add Stick
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-muted-foreground">{sorted.length} sticks in inventory</p>

      {loading ? (
        <p className="text-muted-foreground">Loading sticks…</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50/50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-medium">#</th>
                    <SortHead label="Serial" k="serial" sort={sort} onClick={toggleSort} />
                    <SortHead label="OS Build Version" k="os_build_version" sort={sort} onClick={toggleSort} />
                    <SortHead label="IMEI1" k="imei1" sort={sort} onClick={toggleSort} />
                    <SortHead label="ICCID" k="iccid" sort={sort} onClick={toggleSort} />
                    <SortHead label="MAC ID" k="mac_id" sort={sort} onClick={toggleSort} />
                    <SortHead label="Created Date" k="created_at" sort={sort} onClick={toggleSort} />
                    {canWrite && <th className="px-4 py-3 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {pageItems.length === 0 ? (
                    <tr><td colSpan={canWrite ? 8 : 7} className="p-10 text-center text-muted-foreground">
                      {items.length === 0 ? "No sticks yet. Click Add Stick to add one." : "No sticks match your filters."}
                    </td></tr>
                  ) : pageItems.map((s, idx) => (
                    <tr key={s.id} className="border-b last:border-0 transition-colors hover:bg-slate-50" data-testid={`stick-row-${s.serial}`}>
                      <td className="px-4 py-3 text-slate-500">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="px-4 py-3 font-mono font-medium text-slate-900">{s.serial}</td>
                      <td className="px-4 py-3 font-mono text-slate-700">{s.os_build_version || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-slate-700">{s.imei1 || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-slate-700">{s.iccid || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-slate-700">{s.mac_id || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-slate-500">{(s.created_at || "").slice(0, 10)}</td>
                      {canWrite && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setEditing(s)} data-testid={`stick-edit-${s.serial}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setToDelete(s)} data-testid={`stick-delete-${s.serial}`}>
                              <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
          </div>
        </div>
      )}

      {editing && canWrite && (
        <StickForm stick={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {bulkOpen && canWrite && (
        <BulkImportSheet existing={items} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="stick-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete stick {toDelete?.serial}?</DialogTitle>
            <DialogDescription>This stick will be permanently removed from the inventory. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try { await api.delete(`/stick-inventory/${toDelete.id}`); setToDelete(null); load(); }
                catch (err) { alert(apiError(err)); }
              }}
              data-testid="stick-delete-confirm"
            >
              Delete Stick
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortHead({ label, k, sort, onClick }) {
  const active = sort.key === k;
  return (
    <th className="px-4 py-3 font-medium">
      <button onClick={() => onClick(k)} className="flex items-center gap-1 uppercase tracking-wider">
        {label} <SortIcon dir={active ? sort.dir : null} />
      </button>
    </th>
  );
}

// ------------------------------------------------------------------
// Stick add/edit slide-over
// ------------------------------------------------------------------

function StickForm({ stick, onClose, onSaved }) {
  const isEdit = !!stick;
  const [serial, setSerial] = useState(stick?.serial || "");
  const [osBuild, setOsBuild] = useState(stick?.os_build_version || "");
  const [imei, setImei] = useState(stick?.imei1 || "");
  const [iccid, setIccid] = useState(stick?.iccid || "");
  const [macId, setMacId] = useState(stick?.mac_id || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    if (!serial.trim()) { setError("Serial is required."); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        serial: serial.trim(),
        os_build_version: osBuild.trim() || null,
        imei1: imei.trim() || null,
        iccid: iccid.trim() || null,
        mac_id: macId.trim() || null,
      };
      if (isEdit) await api.put(`/stick-inventory/${stick.id}`, payload);
      else await api.post("/stick-inventory", payload);
      onSaved();
    } catch (err) { setError(apiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="stick-form-sheet">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Stick" : "Add Stick"}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <div>
            <label className="text-sm font-medium">Serial *</label>
            <Input value={serial} onChange={(e) => setSerial(e.target.value)} className="mt-1 font-mono" placeholder="e.g. DD002-00001" data-testid="stick-form-serial" />
          </div>
          <div>
            <label className="text-sm font-medium">OS Build Version</label>
            <Input value={osBuild} onChange={(e) => setOsBuild(e.target.value)} className="mt-1 font-mono" placeholder="e.g. TSIM-004_20260427-1120" data-testid="stick-form-os" />
          </div>
          <div>
            <label className="text-sm font-medium">IMEI1</label>
            <Input value={imei} onChange={(e) => setImei(e.target.value)} className="mt-1 font-mono" placeholder="e.g. 359001234567890" data-testid="stick-form-imei" />
          </div>
          <div>
            <label className="text-sm font-medium">ICC ID</label>
            <Input value={iccid} onChange={(e) => setIccid(e.target.value)} className="mt-1 font-mono" placeholder="e.g. 8901260123456789012" data-testid="stick-form-iccid" />
          </div>
          <div>
            <label className="text-sm font-medium">MAC ID</label>
            <Input value={macId} onChange={(e) => setMacId(e.target.value)} className="mt-1 font-mono" placeholder="e.g. AA:BB:CC:DD:EE:FF" data-testid="stick-form-mac" />
          </div>
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}><X className="mr-1 h-4 w-4" /> Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="stick-form-submit">
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isEdit ? "Save Stick" : "Add Stick"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ------------------------------------------------------------------
// Bulk CSV import slide-over
// ------------------------------------------------------------------

function BulkImportSheet({ existing, onClose, onDone }) {
  const [rows, setRows] = useState(null); // parsed rows
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const existingSerials = useMemo(() => new Set(existing.map((s) => s.serial)), [existing]);

  function normHeader(s) { return (s || "").trim().toLowerCase().replace(/[_-]/g, " "); }

  function parseCsv(text) {
    // Simple CSV parser tolerating quoted fields
    const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
    if (lines.length === 0) return [];
    const split = (line) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQ) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') inQ = false;
          else cur += ch;
        } else {
          if (ch === '"') inQ = true;
          else if (ch === ",") { out.push(cur); cur = ""; }
          else cur += ch;
        }
      }
      out.push(cur);
      return out.map((v) => v.trim());
    };
    const header = split(lines[0]).map(normHeader);
    const idx = (name, aliases = []) => {
      const all = [name, ...aliases];
      for (const alias of all) {
        const i = header.findIndex((h) => h === alias);
        if (i !== -1) return i;
      }
      return -1;
    };
    const iSerial = idx("serial");
    const iOs = idx("os build version", ["os build", "os", "os build ver"]);
    const iImei = idx("imei1", ["imei", "imei 1"]);
    const iIccid = idx("iccid", ["icc id", "icc"]);
    const iMac = idx("mac id", ["mac", "mac address", "macid"]);
    if (iSerial === -1) throw new Error("CSV must have a 'serial' column.");
    const parsed = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = split(lines[i]);
      const serial = (cols[iSerial] || "").trim();
      if (!serial) continue;
      parsed.push({
        serial,
        os_build_version: iOs !== -1 ? (cols[iOs] || "").trim() : "",
        imei1: iImei !== -1 ? (cols[iImei] || "").trim() : "",
        iccid: iIccid !== -1 ? (cols[iIccid] || "").trim() : "",
        mac_id: iMac !== -1 ? (cols[iMac] || "").trim() : "",
      });
    }
    return parsed;
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    try {
      const text = await f.text();
      const parsed = parseCsv(text);
      if (parsed.length === 0) { setError("No valid rows found (rows with a blank serial are skipped)."); setRows([]); return; }
      setRows(parsed);
    } catch (err) { setError(err.message || "Could not read CSV."); }
  }

  async function commit() {
    if (!rows || rows.length === 0) return;
    setUploading(true); setError(null);
    try {
      const { data } = await api.post("/stick-inventory/bulk", { rows });
      setResult(data);
    } catch (err) { setError(apiError(err)); }
    finally { setUploading(false); }
  }

  const preview = rows ? rows.slice(0, 10) : [];

  return (
    <Sheet open onOpenChange={(o) => !o && (result ? onDone() : onClose())}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="stick-bulk-sheet">
        <SheetHeader>
          <SheetTitle>Bulk Import Sticks</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          {!result && (
            <>
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
                <p className="font-semibold text-sky-900">Upload a CSV file</p>
                <p className="mt-1">
                  <span className="font-medium">Columns (in order):</span> <code className="font-mono">Serial</code>, <code className="font-mono">OS Build Version</code>, <code className="font-mono">IMEI1</code>, <code className="font-mono">ICCID</code>, <code className="font-mono">MAC ID</code>
                </p>
                <p className="mt-1 text-sky-800">
                  Header row is auto-detected and skipped. Existing serials are <span className="font-semibold">updated in place</span>; new serials are <span className="font-semibold">added to the bottom</span>. Only <code className="font-mono">Serial</code> is required — the others are optional.
                </p>
              </div>
              <div
                className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center transition hover:border-slate-400 hover:bg-slate-100 cursor-pointer"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f && fileRef.current) {
                    const dt = new DataTransfer();
                    dt.items.add(f);
                    fileRef.current.files = dt.files;
                    fileRef.current.dispatchEvent(new Event("change", { bubbles: true }));
                  }
                }}
              >
                <Upload className="mx-auto h-8 w-8 text-slate-400" />
                <p className="mt-3 text-sm font-medium text-slate-700">Drop your CSV here, or click to browse</p>
                <p className="mt-1 text-xs text-slate-500">Supports files with 1,000+ rows</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFile}
                  className="hidden"
                  data-testid="stick-bulk-file"
                />
              </div>
              {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
              {rows && rows.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-slate-700">Preview (first 10 of {rows.length})</p>
                  <div className="overflow-hidden rounded-md border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-left text-slate-500">
                        <tr>
                          <th className="px-2 py-2 font-medium">Serial</th>
                          <th className="px-2 py-2 font-medium">OS Build</th>
                          <th className="px-2 py-2 font-medium">IMEI1</th>
                          <th className="px-2 py-2 font-medium">ICCID</th>
                          <th className="px-2 py-2 font-medium">MAC ID</th>
                          <th className="px-2 py-2 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 font-mono">{r.serial}</td>
                            <td className="px-2 py-1.5 font-mono">{r.os_build_version || "—"}</td>
                            <td className="px-2 py-1.5 font-mono">{r.imei1 || "—"}</td>
                            <td className="px-2 py-1.5 font-mono">{r.iccid || "—"}</td>
                            <td className="px-2 py-1.5 font-mono">{r.mac_id || "—"}</td>
                            <td className="px-2 py-1.5">
                              {existingSerials.has(r.serial)
                                ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Update</Badge>
                                : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">New</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
                    <Button onClick={commit} disabled={uploading} data-testid="stick-bulk-confirm">
                      {uploading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                      Confirm Upload ({rows.length} rows)
                    </Button>
                  </div>
                </div>
              )}
              {(!rows || rows.length === 0) && (
                <div className="flex justify-end">
                  <Button variant="outline" onClick={onClose}>Close</Button>
                </div>
              )}
            </>
          )}
          {result && (
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <p className="font-semibold">Import complete</p>
                <ul className="mt-2 space-y-0.5">
                  <li>Inserted: <span className="font-semibold">{result.inserted}</span></li>
                  <li>Updated: <span className="font-semibold">{result.updated}</span></li>
                  <li>Skipped (blank serial): <span className="font-semibold">{result.skipped}</span></li>
                </ul>
              </div>
              <div className="flex justify-end">
                <Button onClick={onDone}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
